// src/pages/SelectionOrders/components/SelectionOrderCreate.tsx
import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import useSelectionOrderService from '../../../app/services/hooks/useSelectionOrderService';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import SelectionOrderForm from './forms/SelectionOrderForm';
import { SelectionOrderDataForm, SelectionOrderStorePayload } from '../../../app/models/SelectionOrder';

interface Props {
  onSaved?: () => void;
  onCancel?: () => void;
  onError?: () => void;
}

type SelectOpt = { id: number | string; name: string };
const NOOP = () => undefined;

const SelectionOrderCreate = ({ onSaved, onCancel, onError }: Props) => {
  const { showLoading, hideLoading } = useContext(AppContext);
  const { createSelectionOrder, storeSelectionOrder, fetchingStoreSelectionOrder } = useSelectionOrderService();

  const handleCancel = onCancel ?? NOOP;

  const [order, setOrder] = useState<SelectionOrderDataForm>({
    society_id: null,
    program_date: '',
    start_date: null,
    end_date: null,
    status: 'CREATED',
    is_periodic: false,
    recurrence_days: [],
    batch_id: null,
    quantity: null as any,
    unit_id: null as any,
    collector_user_id: null,
    is_active: true,
    planned_quantity: null as any,
    planned_unit_id: null as any,
  });

  const [societies, setSocieties] = useState<SelectOpt[]>([]);
  const [units, setUnits] = useState<SelectOpt[]>([]);
  const [statuses, setStatuses] = useState<SelectOpt[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  useEffect(() => { create(); }, []);

  const create = () => {
    showLoading?.('loading', 'Cargando datos de formulario...');
    createSelectionOrder({
      onSuccess: (response: ServiceResponse) => {
        setSocieties(response.data.societies || []);
        setUnits(response.data.units || []);
        setStatuses(response.data.status || []);
        hideLoading?.();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
        onError && onError();
      }
    });
  };

  const store = () => {
    if (!order.society_id) { toast.error('Debe seleccionar una sociedad'); return; }
    if (!order.program_date) { toast.error('Debe indicar la fecha de programa'); return; }
    if (order.is_periodic) {
      if (!order.recurrence_days?.length) { toast.error('Debe seleccionar al menos un día de la semana'); return; }
      if (!order.end_date) { toast.error('Debe indicar la fecha hasta la cual crear órdenes'); return; }
      if (order.end_date < order.program_date) { toast.error('La fecha "Hasta el" debe ser igual o posterior a la fecha de inicio'); return; }
    }

    const base: SelectionOrderStorePayload = {
      society_id: order.society_id as number,
      program_date: order.program_date as string,
      ...(order.start_date ? { start_date: order.start_date as string } : {}),
      ...(order.status ? { status: order.status as any } : {}),
    };

    const withPlanning: SelectionOrderStorePayload = {
      ...base,
      ...(
        order.planned_quantity != null && order.planned_unit_id != null
          ? {
              planned_quantity: Number(order.planned_quantity),
              planned_unit_id: order.planned_unit_id as number,
            }
          : {}
      ),
    };

    const finalPayload: SelectionOrderStorePayload = order.is_periodic
      ? {
          ...withPlanning,
          is_periodic: true,
          recurrence_days: order.recurrence_days ?? [],
          end_date: order.end_date ?? null,
        }
      : withPlanning;

    showLoading?.('loading', order.is_periodic ? 'Creando órdenes...' : 'Guardando orden...');
    storeSelectionOrder(finalPayload, {
      onSuccess: (response: ServiceResponse) => {
        hideLoading?.();
        const count = response.data?.created_count ?? 1;
        toast.success(response.message || (count > 1 ? `Se crearon ${count} órdenes.` : 'Orden creada.'), { autoClose: 2500 });
        onSaved && onSaved();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoading?.();
        setErrorFields(errs.data);
      }
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <SelectionOrderForm
            dataForm={order}
            setDataForm={setOrder}
            societies={societies}
            units={units}
            statuses={statuses}
            errorFields={errorFields}
          />
        </div>
      </div>
      <hr />
      <div className="row justify-content-end">
        <div className="col-auto">
          <ButtonCancelForm callbackCancel={handleCancel} locked={!!fetchingStoreSelectionOrder} />
        </div>
        <div className="col-auto">
          <ButtonSaveForm callbackSave={store} locked={!!fetchingStoreSelectionOrder} />
        </div>
      </div>
    </>
  );
};

export default SelectionOrderCreate;